import isNil from 'lodash-es/isNil';
import isNumber from 'lodash-es/isNumber';
/** LOCAL CONSTANTS */
const ABSOLUTE_ZERO = 273.15;
const MM_INCHES_RELATION = 25.4;
const KPH_MILES_RELATION = 1.609344;
const KPH_MPS_RELATION = 3.6;
const SECONDS_IN_HOUR = 3600;
const createConverter = (converter) => (value) => isNil(value) || !isNumber(value) ? null : converter(value);
/** TEMPERATURES */
const celsiusToFahrenheit = createConverter((celsius) => (celsius * 9) / 5 + 32);
const fahrenheitToCelsius = createConverter((fahrenheit) => (fahrenheit - 32) * (5 / 9));
const milliSiemensPerMeterToDeciSiemensPerMeter = createConverter((milliSiemens) => milliSiemens / 100);
const deciSiemensPerMeterToMilliSiemensPerMeter = createConverter((deciSiemensPerMeter) => deciSiemensPerMeter * 100);
const celsiusToKelvins = createConverter((celsius) => celsius + ABSOLUTE_ZERO);
const kelvinToCelsius = createConverter((kelvin) => kelvin - ABSOLUTE_ZERO);
const celsiusDegreesDayToFahrenheitDegreesDay = createConverter((celsiusDegreesDay) => (celsiusDegreesDay * 9) / 5);
/**  Fahrenheit Degrees Day To Celsius Degrees Day */
const fddToCdd = createConverter((fdd) => (fdd * 5) / 9);
/** LENGTHS */
const millimetersToInches = createConverter((mm) => mm / MM_INCHES_RELATION);
const inchesToMillimeters = createConverter((inches) => inches * MM_INCHES_RELATION);
/** SPEEDS */
const kilometersPerHourToMilesPerHour = createConverter((kmh) => kmh / KPH_MILES_RELATION);
/** Miles per hour to Kilometers per hour */
const mphToKph = createConverter((mph) => mph * KPH_MILES_RELATION);
const kilometersPerHourToMetersPerSecond = createConverter((kmh) => kmh / KPH_MPS_RELATION);
/** Meters per seconds to Kilometers per hour */
const mpsToKph = createConverter((mps) => mps * KPH_MPS_RELATION);
const kilometersPerHourToBeaufort = createConverter((kmh) => {
    if (kmh < 1)
        return 0;
    if (kmh < 6)
        return 1;
    if (kmh < 12)
        return 2;
    if (kmh < 20)
        return 3;
    if (kmh < 29)
        return 4;
    if (kmh < 39)
        return 5;
    if (kmh < 50)
        return 6;
    if (kmh < 62)
        return 7;
    if (kmh < 75)
        return 8;
    if (kmh < 89)
        return 9;
    if (kmh < 103)
        return 10;
    if (kmh < 118)
        return 11;
    return 12;
});
const beaufortToKph = createConverter((beaufort) => (beaufort * 3) ^ (3 / 2));
/** ELECTRIC CURRENTS */
const minVoltageByLevel = {
    0: 0,
    1: 362,
    2: 416,
    3: 470,
    4: 525,
    5: 579,
    6: 633,
    7: 687,
    8: 742,
    9: 796,
    10: 850
};
/**
 * Convert an intensity in millivolts (mV) into an LWS (Leaf Wetness Sensor) intensity level from 1 to 10
 *
 * @param {int} value in millivolts (mV) to convert.
 * @returns {int} value converted into an LWS intensity level
 *
 * @example intensityInVoltsToLWSIntensityLevel(362) => 1
 */
const intensityInMvToLWSIntensityLevel = createConverter((volts) => {
    if (volts >= minVoltageByLevel[10])
        return 10;
    if (volts >= minVoltageByLevel[9])
        return 9;
    if (volts >= minVoltageByLevel[8])
        return 8;
    if (volts >= minVoltageByLevel[7])
        return 7;
    if (volts >= minVoltageByLevel[6])
        return 6;
    if (volts >= minVoltageByLevel[5])
        return 5;
    if (volts >= minVoltageByLevel[4])
        return 4;
    if (volts >= minVoltageByLevel[3])
        return 3;
    if (volts >= minVoltageByLevel[2])
        return 2;
    if (volts >= minVoltageByLevel[1])
        return 1;
    return 0;
});
/** TIME */
const secondsToHours = createConverter((s) => 
// using floor to only get fully completed hours
Math.floor(s / SECONDS_IN_HOUR));
const hoursToSeconds = createConverter((h) => h * SECONDS_IN_HOUR);
/** DISTANCE */
const kilometersToMiles = createConverter((k) => k / KPH_MILES_RELATION);
const milesToKilometers = createConverter((k) => k * KPH_MILES_RELATION);
/** */
const undefinedToNull = (value) => (value === undefined ? null : value);
export const defaultConverter = undefinedToNull;
export const availableConverters = {
    electricalConductivity: (value, unit) => {
        switch (unit) {
            case 'decisiemens_per_meter': {
                return milliSiemensPerMeterToDeciSiemensPerMeter(value);
            }
            case 'millisiemens_per_meter':
            default: {
                return defaultConverter(value);
            }
        }
    },
    temperature: (value, unit) => {
        switch (unit) {
            case 'degrees_fahrenheit': {
                return celsiusToFahrenheit(value);
            }
            case 'kelvins': {
                return celsiusToKelvins(value);
            }
            default: {
                return defaultConverter(value);
            }
        }
    },
    length: (value, unit) => {
        switch (unit) {
            case 'inches': {
                return millimetersToInches(value);
            }
            default: {
                return defaultConverter(value);
            }
        }
    },
    windSpeed: (value, unit) => {
        switch (unit) {
            case 'meters_per_second': {
                return kilometersPerHourToMetersPerSecond(value);
            }
            case 'miles_per_hour': {
                return kilometersPerHourToMilesPerHour(value);
            }
            case 'beaufort': {
                return kilometersPerHourToBeaufort(value);
            }
            default: {
                return defaultConverter(value);
            }
        }
    },
    heatAccumulation: (value, unit) => {
        switch (unit) {
            case 'fahrenheit_degrees_day':
                return celsiusDegreesDayToFahrenheitDegreesDay(value);
            default:
                return defaultConverter(value);
        }
    },
    leafWetness: (value, unit) => {
        switch (unit) {
            case 'lws_intensity_level':
                return intensityInMvToLWSIntensityLevel(value);
            default:
                return defaultConverter(value);
        }
    },
    rainfallIntensity: (value, unit) => {
        switch (unit) {
            case 'inches_per_hour':
                return millimetersToInches(value);
            default:
                return defaultConverter(value);
        }
    },
    degrees: defaultConverter,
    pressure: defaultConverter,
    leafWetnessDuration: (value, unit) => {
        switch (unit) {
            case 'hours':
                return secondsToHours(value);
            default:
                return defaultConverter(value);
        }
    },
    surfacePowerDensity: defaultConverter,
    photosyntheticActiveRadiation: defaultConverter,
    percent: defaultConverter,
    voltage: defaultConverter,
    time: (value, unit) => {
        switch (unit) {
            case 'hours':
                return secondsToHours(value);
            default:
                return defaultConverter(value);
        }
    },
    distance: (value, unit) => {
        switch (unit) {
            case 'miles':
                return kilometersToMiles(value);
            default:
                return defaultConverter(value);
        }
    }
};
/**
 * use to convert unit to the unit used by backend
 */
export const toDefaultUnitConverters = {
    electricalConductivity: (value, unit) => {
        switch (unit) {
            case 'decisiemens_per_meter': {
                return deciSiemensPerMeterToMilliSiemensPerMeter(value);
            }
            case 'millisiemens_per_meter':
            default: {
                return defaultConverter(value);
            }
        }
    },
    temperature: (value, unit) => {
        switch (unit) {
            case 'degrees_fahrenheit': {
                return fahrenheitToCelsius(value);
            }
            case 'kelvins': {
                return kelvinToCelsius(value);
            }
            default: {
                return defaultConverter(value);
            }
        }
    },
    length: (value, unit) => {
        switch (unit) {
            case 'inches': {
                return inchesToMillimeters(value);
            }
            default: {
                return defaultConverter(value);
            }
        }
    },
    windSpeed: (value, unit) => {
        switch (unit) {
            case 'meters_per_second': {
                return mpsToKph(value);
            }
            case 'miles_per_hour': {
                return mphToKph(value);
            }
            case 'beaufort': {
                return beaufortToKph(value);
            }
            default: {
                return defaultConverter(value);
            }
        }
    },
    heatAccumulation: (value, unit) => {
        switch (unit) {
            case 'fahrenheit_degrees_day':
                return fddToCdd(value);
            default:
                return defaultConverter(value);
        }
    },
    /** cannot convert from LW_I to LW_V so we return the given value */
    leafWetness: (value) => {
        return Object.entries(minVoltageByLevel).reduce((acc, [levelAsString, voltage]) => {
            const level = parseInt(levelAsString, 10);
            if (value === level) {
                acc = voltage;
            }
            return acc;
        }, null);
    },
    rainfallIntensity: (value, unit) => {
        switch (unit) {
            case 'inches_per_hour':
                return inchesToMillimeters(value);
            default:
                return defaultConverter(value);
        }
    },
    degrees: defaultConverter,
    pressure: defaultConverter,
    leafWetnessDuration: (value, unit) => {
        switch (unit) {
            case 'hours':
                return hoursToSeconds(value);
            default:
                return defaultConverter(value);
        }
    },
    percent: defaultConverter,
    voltage: defaultConverter,
    time: (value, unit) => {
        switch (unit) {
            case 'hours':
                return hoursToSeconds(value);
            default:
                return defaultConverter(value);
        }
    },
    surfacePowerDensity: defaultConverter,
    photosyntheticActiveRadiation: defaultConverter,
    distance: (value, unit) => {
        switch (unit) {
            case 'miles':
                return milesToKilometers(value);
            default:
                return defaultConverter(value);
        }
    }
};
